import {
  MembershipStatus,
  MenuAction,
  OrganizationMembership,
} from "@inductiveautomation/ws-components";
import i18n from "../../i18n/i18n";
import {
  OrganizationActionCallback,
  OrganizationActionType,
} from "./Organizations.types";

const createMenuAction =
  (onClick: OrganizationActionCallback, organizationId: string) =>
  (
    type: Exclude<
      OrganizationActionType,
      | "create_organization"
      | "create_organization_with_existing_organizations"
      | "leave_multiple_organizations"
    >,
    icon: MenuAction["iconLeft"],
    options: {
      destructive?: boolean;
      bulkAction?: boolean;
      externalLink?: boolean;
    } = {}
  ): MenuAction => {
    const {
      destructive = false,
      bulkAction = true,
      externalLink = false,
    } = options;

    const label = i18n.t(`organizations.actions.${type}.button_label`);

    return {
      id: `organization-action-${type}`,
      iconLeft: icon,
      iconLeftVariant: destructive ? "destructive" : undefined,
      iconRight: externalLink ? "open-small" : undefined,
      iconRightVariant: externalLink ? "neutralLight" : undefined,
      bulkAction,
      label: i18n.t(`organizations.actions.${type}.button_label`),
      bulkLabel:
        type === "launch_workspace"
          ? label
          : i18n.t(`organizations.actions.${type}.button_bulk_label`),
      onClick: (_: string, ids: string[]) => {
        if (ids.length > 1) {
          // Bulk action from AdvancedDataGridHeader
          void onClick(type, ids);
        } else {
          // Single action: Use the organization's ID instead of the passed `ids` (which is the action ID).
          void onClick(type, [organizationId]);
        }
      },
    };
  };

/*
 * Builds the actions used in the drawer footer menu and data card menu (which provide the actions for the data grid
 * header).
 */
export const actionsForMembership = (
  membership: OrganizationMembership,
  onClick: OrganizationActionCallback
): MenuAction[] => {
  const status: MembershipStatus = membership.membershipStatus;
  const menuAction = createMenuAction(onClick, membership.organization.orgId);

  if (status === "AVAILABLE") {
    return [menuAction("request_to_join", "email-outlined")];
  }
  if (status === "PENDING") {
    return [
      menuAction("resend_request", "email-outlined-notification"),
      menuAction("cancel_request", "email-outlined-remove", {
        destructive: true,
      }),
    ];
  }
  if (status === "INVITED") {
    return [menuAction("accept_invite", "email-outlined-check")];
  }

  return [
    menuAction("launch_workspace", "organization-inverse", {
      bulkAction: false,
      externalLink: true,
    }),
    menuAction("leave_organization", "organization-remove-outlined", {
      destructive: true,
    }),
  ];
};
